import { useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Transition, TransitionStatus } from 'react-transition-group';
import {
  IActivityProps,
  IGroupedActivityProps,
  IUserProps,
  NotificationGroupType,
  TaskType
} from 'types';
/* eslint-disable react/jsx-props-no-spreading */
import { ActionButton, ActivityItem, IconButton } from '@fluentui/react';
import Activity from './Activity';

interface GroupedActivityContainerProps {
  callInterpolationFunction: (functionName: string, activity: IActivityProps) => JSX.Element;
  getActivityIcon: (type: number) => JSX.Element;
  getCreatorTooltip: (activity: IActivityProps) => JSX.Element;
  groupedActivity: IGroupedActivityProps;
  handleOnActivityClick?: (activity: IActivityProps) => void;
  handleOnGroupedActivityClick?: (groupedActivity: IGroupedActivityProps) => void;
  handleOnGroupedReadIconClick?: (groupedActivity: IGroupedActivityProps) => void;
  handleOnReadIconClick?: (activity: IActivityProps) => void;
  timeAgo: (value: string, format?: string) => string;
}

export default function GroupedActivityContainer({
  callInterpolationFunction,
  getActivityIcon,
  getCreatorTooltip,
  groupedActivity,
  handleOnActivityClick,
  handleOnGroupedActivityClick,
  handleOnGroupedReadIconClick,
  handleOnReadIconClick,
  timeAgo
}: GroupedActivityContainerProps): JSX.Element | null {
  const { t } = useTranslation();

  const groupChildrenRef = useRef() as React.MutableRefObject<HTMLInputElement>;

  const [isGroupOpen, setIsGroupOpen] = useState(false);
  const [childrenHeight, setChildrenHeight] = useState(0);

  useLayoutEffect(() => {
    if (groupChildrenRef.current) {
      setChildrenHeight(groupChildrenRef.current.clientHeight);
    }
  }, []);

  const childrenLength = groupedActivity.notifications.length;

  function getGroupedActivityIcon(): JSX.Element {
    if (groupedActivity.notifications.length === 1) {
      // icon just like a single activity
      return getActivityIcon(groupedActivity.notifications[0].type);
    }

    if (groupedActivity.type === 1) {
      // task related
      return getActivityIcon(1);
    }

    if (groupedActivity.type === 2) {
      // team related
      return getActivityIcon(21);
    }

    if (groupedActivity.type === 3) {
      // definition related
      return getActivityIcon(38);
    }

    if (groupedActivity.type === 4) {
      // instance related
      return getActivityIcon(36);
    }

    // default get the first activity
    return getActivityIcon(groupedActivity.notifications[childrenLength - 1].type);
  }

  const activityItemProps = {
    activityDescription: getGroupedActivityDescription(groupedActivity),
    comments: null, // getActivityComments(activity),
    activityIcon: getGroupedActivityIcon(),
    timeStamp: timeAgo(groupedActivity.notifications[childrenLength - 1]?.creationDate || '')
  };

  const hasUnreadActivity = !!groupedActivity.notifications.find(
    (activity: IActivityProps) => activity.isNew
  );

  function getTaskTypeDescription(typeId?: number | null): string {
    // default to 1
    const taskType = typeId || TaskType.Task;

    return t(`activitiesList.task.type.${taskType}`);
  }

  function getDistinctCreators(notifications: IActivityProps[]): IUserProps[] | never[] {
    const distinctCreators: IUserProps[] = [];

    notifications.map((notification): undefined => {
      const alreadyPushed = distinctCreators.find(
        (dc) => dc.userId === notification.creator?.userId
      );

      if (!alreadyPushed && notification.creator) {
        distinctCreators.push(notification.creator);
      }

      return undefined;
    });

    return distinctCreators;
  }

  function getGroupedActivityTaskDescription(
    groupedActivity: IGroupedActivityProps
  ): JSX.Element[] {
    const { notifications } = groupedActivity;

    // deleted > created > updated

    const taskDeleteTypes = [33, 34, 44, 49, 50, 53, 54, 56, 58, 60, 62, 64, 66, 68];
    const deletedTaskNotification = notifications.find((notification: IActivityProps) =>
      taskDeleteTypes.includes(notification.type)
    );

    const taskCreateTypes = [1, 30, 43, 45, 46, 51, 52, 55, 57, 59, 61, 63, 65, 67];
    const createdTaskNotification = notifications.find((notification: IActivityProps) =>
      taskCreateTypes.includes(notification.type)
    );

    let translation: string | null = null;
    const description = [];

    if (deletedTaskNotification) {
      // create deleted task group header description
      description.push(getCreatorTooltip(deletedTaskNotification));

      translation = t('activitiesList.group.header.task', {
        context: 'deleted',
        taskType: getTaskTypeDescription(deletedTaskNotification.oldTask?.typeId),
        taskTitle: deletedTaskNotification.oldTask?.title || ''
      });
    } else if (createdTaskNotification) {
      // create created task group header description
      description.push(getCreatorTooltip(createdTaskNotification));

      translation = t('activitiesList.group.header.task', {
        context: 'created',
        taskType: getTaskTypeDescription(createdTaskNotification.newTask?.typeId),
        taskTitle: createdTaskNotification.newTask?.title || ''
      });
    } else {
      // create updated task group header description
      const firstUpdateNotification = notifications[notifications.length - 1];
      description.push(getCreatorTooltip(firstUpdateNotification));

      const distinctCreators = getDistinctCreators(
        // remove first creator
        notifications.filter(
          (n: IActivityProps) => n.creator?.userId !== firstUpdateNotification.creator?.userId
        )
      );

      const taskType = getTaskTypeDescription(firstUpdateNotification.newTask?.typeId);
      const taskTitle =
        firstUpdateNotification.oldTask?.title || firstUpdateNotification.newTask?.title;

      translation = t('activitiesList.group.header.task', {
        context: 'updated',
        count: distinctCreators.length > 0 ? distinctCreators.length : undefined,
        taskType,
        taskTitle
      });
    }

    const { id } = notifications[0];

    if (translation && id) {
      description.push(getDescriptionSpan(id, translation));
    }

    return description;
  }

  function getGroupedActivityRouteInstanceDescription(
    groupedActivity: IGroupedActivityProps
  ): JSX.Element[] {
    const { notifications } = groupedActivity;
    const firstUpdateNotification = notifications[notifications.length - 1];

    const translationsArray: string[] = t(
      `activitiesList.activity.${firstUpdateNotification.type}.description`,
      {
        activity: firstUpdateNotification,
        returnObjects: true
      }
    );

    return translationsArray.map((translation) => {
      if (translation.indexOf('fx.') === 0) {
        const interpolationFunctionName: string = translation.split('.')[1];

        return callInterpolationFunction(interpolationFunctionName, firstUpdateNotification);
      }

      return getDescriptionSpan(firstUpdateNotification.id || '', translation);
    });
  }

  function getGroupedActivityRouteDefinitionDescription(
    groupedActivity: IGroupedActivityProps
  ): JSX.Element[] {
    // deleted > created > updated
    const { notifications } = groupedActivity;
    const firstUpdateNotification = notifications[notifications.length - 1];

    const deletedRouteDefinitionNotification = notifications.find(
      (notification: IActivityProps) => notification.type === 37
    );

    const createdRouteDefinitionNotification = notifications.find(
      (notification: IActivityProps) => notification.type === 35
    );

    let translation: string | null = null;
    const description = [];

    if (deletedRouteDefinitionNotification) {
      // create deleted routeDefinition group header description
      description.push(getCreatorTooltip(deletedRouteDefinitionNotification));

      translation = t('activitiesList.group.header.routeDefinition', {
        context: 'deletedb',
        activity: firstUpdateNotification
      });
    } else if (createdRouteDefinitionNotification) {
      // create created routeDefinition group header description
      description.push(getCreatorTooltip(createdRouteDefinitionNotification));

      translation = t('activitiesList.group.header.routeDefinition', {
        context: 'created',
        activity: firstUpdateNotification
      });
    } else {
      // create updated routeDefinition group header description
      description.push(getCreatorTooltip(firstUpdateNotification));

      const distinctCreators = getDistinctCreators(
        // remove first creator
        notifications.filter(
          (n: IActivityProps) => n.creator?.userId !== firstUpdateNotification.creator?.userId
        )
      );

      translation = t('activitiesList.group.header.routeDefinition', {
        context: 'updated',
        count: distinctCreators.length > 0 ? distinctCreators.length : undefined,
        activity: firstUpdateNotification
      });
    }
    const { id } = notifications[0];
    if (translation && id) {
      description.push(getDescriptionSpan(id, translation));
    }

    return description;
  }

  function getGroupedActivityTeamDescription(
    groupedActivity: IGroupedActivityProps
  ): JSX.Element[] {
    // deleted > created > updated
    const { notifications } = groupedActivity;
    const firstUpdateNotification = notifications[notifications.length - 1];

    const deletedTeamNotification = notifications.find(
      (notification: IActivityProps) => notification.type === 26
    );

    const createdTeamNotification = notifications.find(
      (notification: IActivityProps) => notification.type === 21
    );

    let translation: string | null = null;
    const description = [];

    if (deletedTeamNotification) {
      // create deleted team group header description
      description.push(getCreatorTooltip(deletedTeamNotification));

      translation = t('activitiesList.group.header.team', {
        context: 'deleteda',
        activity: firstUpdateNotification
      });
    } else if (createdTeamNotification) {
      // create created team group header description
      description.push(getCreatorTooltip(createdTeamNotification));

      translation = t('activitiesList.group.header.team', {
        context: 'created',
        activity: firstUpdateNotification
      });
    } else {
      // create updated team group header description
      description.push(getCreatorTooltip(firstUpdateNotification));

      const distinctCreators = getDistinctCreators(
        // remove first creator
        notifications.filter(
          (n: IActivityProps) => n.creator?.userId !== firstUpdateNotification.creator?.userId
        )
      );

      translation = t('activitiesList.group.header.team', {
        context: 'updated',
        count: distinctCreators.length > 0 ? distinctCreators.length : undefined,
        activity: firstUpdateNotification
      });
    }

    const { id } = notifications[0];

    if (translation && id) {
      description.push(getDescriptionSpan(id, translation));
    }

    return description;
  }

  function getDescriptionSpan(id: string, translation: string): JSX.Element {
    return (
      <span key={`activity-header-description-${id}-text`} className="activity-description">
        {translation}
      </span>
    );
  }

  function getGroupedActivityDescription(groupedActivity: IGroupedActivityProps): JSX.Element[] {
    // get description based on notification group type
    if (groupedActivity.type === NotificationGroupType.Task) {
      return getGroupedActivityTaskDescription(groupedActivity);
    }

    if (groupedActivity.type === NotificationGroupType.Team) {
      return getGroupedActivityTeamDescription(groupedActivity);
    }

    if (groupedActivity.type === NotificationGroupType.RouteInstance) {
      return getGroupedActivityRouteInstanceDescription(groupedActivity);
    }

    if (groupedActivity.type === NotificationGroupType.RouteDefinition) {
      return getGroupedActivityRouteDefinitionDescription(groupedActivity);
    }

    return [];
  }

  function onReadIconClick(
    activity?: IActivityProps,
    groupedActivity?: IGroupedActivityProps
  ): void {
    if (activity && !!handleOnReadIconClick) {
      handleOnReadIconClick(activity);
    }

    if (groupedActivity && !!handleOnGroupedReadIconClick) {
      handleOnGroupedReadIconClick(groupedActivity);
    }
  }

  function getNewActivityIcons(
    activity?: IActivityProps,
    groupedActivity?: IGroupedActivityProps
  ): JSX.Element {
    return (
      <div className="new-notification-container">
        <IconButton
          iconProps={{ iconName: 'RedEye' }}
          onClick={() => onReadIconClick(activity, groupedActivity)}
          className="read-notification-button"
          title={t('activitiesList.mark-as-read')}
        />
        <div className="new-notification-indicator" />
      </div>
    );
  }

  function onGroupedActivityClick(groupedActivity: IGroupedActivityProps): void {
    if (handleOnGroupedActivityClick) {
      handleOnGroupedActivityClick(groupedActivity);
    }
  }

  function getGroupedActivityHeader() {
    const key = `${groupedActivity.notifications[0].id}-header`;

    return (
      <div style={{ paddingBottom: '7px' }}>
        <div
          key={key}
          className={`activity-list-item ${hasUnreadActivity ? 'unread-notification' : ''}`}
        >
          <div
            aria-hidden="true"
            onClick={() => onGroupedActivityClick(groupedActivity)}
            className={`activity-item ${handleOnGroupedActivityClick ? 'active' : ''}`}
          >
            <ActivityItem {...activityItemProps} />
          </div>
          {getNewActivityIcons(undefined, groupedActivity)}
          <ActionButton
            styles={{
              root: {
                bottom: 5,
                height: 'fit-content',
                minHeight: 10,
                minWidth: 150,
                position: 'absolute',
                right: 0,
                width: 'fit-content'
              },
              flexContainer: { fontSize: 10 }
            }}
            onClick={() => {
              setIsGroupOpen((prevState) => !prevState);
            }}
            text={t('activitiesList.group.header.moreActivitiesButton', {
              activitiesCount: childrenLength
            })}
          />
        </div>
      </div>
    );
  }

  if (!groupedActivity.notifications.length) {
    return null;
  }

  if (groupedActivity.notifications.length === 1) {
    return (
      <Activity
        activity={groupedActivity.notifications[0]}
        callInterpolationFunction={callInterpolationFunction}
        getActivityIcon={getActivityIcon}
        handleOnActivityClick={handleOnActivityClick}
        handleOnReadIconClick={handleOnReadIconClick}
        key={groupedActivity.notifications[0].id}
        timeAgo={timeAgo}
      />
    );
  }

  const children = groupedActivity.notifications.map((activity: IActivityProps) => {
    return (
      <Activity
        activity={activity}
        callInterpolationFunction={callInterpolationFunction}
        getActivityIcon={getActivityIcon}
        handleOnActivityClick={handleOnActivityClick}
        handleOnReadIconClick={handleOnReadIconClick}
        key={activity.id}
        timeAgo={timeAgo}
      />
    );
  });

  function getActivityChildren(): JSX.Element {
    const transitionStyles = {
      unmounted: { height: childrenHeight, opacity: 1 },
      entering: { height: childrenHeight, opacity: 1 },
      entered: { height: 'auto', opacity: 1 },
      exiting: { height: childrenHeight, opacity: 0 },
      exited: { height: 0, opacity: 0 }
    };

    const duration = 300;
    return (
      <Transition in={isGroupOpen} timeout={{ appear: 0, enter: duration, exit: 0 }}>
        {(state: TransitionStatus) => {
          return (
            <div
              style={{
                overflow: 'hidden',
                transition: `height ${duration}ms ease-out, opacity ${duration}ms ease-out`,
                marginLeft: '15px',
                ...transitionStyles[state]
              }}
            >
              {children}
            </div>
          );
        }}
      </Transition>
    );
  }

  if (!childrenHeight) {
    // return children to mesure height for transitions
    return (
      <div
        style={{ overflow: 'hidden', marginLeft: '15px', height: 'auto' }}
        ref={groupChildrenRef}
      >
        {children}
      </div>
    );
  }

  return (
    <>
      {getGroupedActivityHeader()}
      {getActivityChildren()}
    </>
  );
}
